import { AbstractCrudService } from 'app/services/abstract-crud/abstract-crud.service';
import { Action, StateContext, createSelector, Selector } from '@ngxs/store';
import { catchError, tap } from 'rxjs/operators';
import { EntityActions as EA } from './entity.action';
import { of } from 'rxjs';

// Extend this with a <T>ype in your concrete subclass.
export class EntityStateModel<T> {
	entities: T[];
  current: T | null;
  expires: number;
  loading?: boolean;
  error?: any;
}

/**
 * Entity defaults are defined in the concrete subclass to have a unique name
 * key in the store. You can copy/paste this block and uncomment it.
 *
  @State<EntityStateModel<YOUR_ENTITY_TYPE_HERE>>({
    name: 'entity_name',  // singular, not plural
    defaults: {
      entities: [],
      current: null,
      expires: 0
    }
  })
 */

// All entities of type T must extend the EntityInterace by including an ID
// to make it searchable here.
export class EntityState<T extends { id?: number }> {

  /*** SELECTORS AND STATIC METHODS ***/
  // Static methods use U to distinguish from T in class instances.
  static entities<U>() {
    return createSelector([this], (state: { entities: U[] }) => {
      return state.entities;
    });
  }

  static current<U>() {
    return createSelector([this], (state: { current: U | null }) => {
      return state.current;
    });
  }

  static expires() {
    return createSelector([this], (state: { expires: number }) => {
      return state.expires;
    });
  }

  // Get entity by ID.
  static entity<U extends { id: number }>(id: number) {
    return createSelector([this], (state: { entities: U[] }) => {
      return state.entities.find(e => e.id === id);
    });
  }

  /**
   * CONSTRUCTOR - must be after selectors above.
   *
   * @param entityService concrete service subclass.
   * @param expiresIn seconds from now defaults to 5 min
   */
	constructor(
    private entityService: AbstractCrudService<T>,
    private expiresIn: number = (5 * 60 * 1000),
  ) {}

  /*** ACTIONS. See entity.actions.ts for declarations. ***/

  // Get an entity by id and set to current.
	@Action(EA.Get)
	get(ctx: StateContext<EntityStateModel<T>>, payload: EA.Get) {
    const state = ctx.getState();
    const id = payload.id;

    // If already exists and not expired, return.
    const found = state.entities.find(e => e.id === id);
    if (found && state.expires > Date.now()) {
      ctx.patchState({
        current: found,
      });
      return;
    }

    ctx.patchState({
      loading: true,
    });

    // Fetch it with its service, update expiration, and make it current.
    return this.entityService.get(id).pipe(
      tap(entity => {
        // Add to current list of entities in store.
        const entities = [...state.entities, entity];
        ctx.patchState({
          entities,
          expires: Date.now() + this.expiresIn,
          current: entity,
          loading: false,
        });
      }),
      catchError( error => {
        ctx.patchState({
          loading: false,
          error,
        });
        return of(error);
      })
    );
	}

  // Get multiple entities. If no argument provided, back end should
  // get all for the authenticated user.
  // CAREFUL! This may need to be limited (pagination, etc.) for some cases.
  @Action(EA.GetIndex)
	getIndex(ctx: StateContext<EntityStateModel<T>>, payload: EA.GetIndex) {
    const state = ctx.getState();
    // If already exists and not expired, return and get from local store.
    if (state.expires > Date.now()) {
      return;
    }

    ctx.patchState({
      loading: true,
    });

    // Fetch it with its service, update expiration, and make it current.
    return this.entityService.getIndex().pipe(
      tap(entities => {
        ctx.patchState({
          entities,
          expires: Date.now() + this.expiresIn,
          loading: false,
        });
      }),
      catchError( error => {
        ctx.patchState({
          loading: false,
          error,
        });
        return of(error);
      })
    );
	}

  @Action(EA.Create)
  create(ctx: StateContext<EntityStateModel<T>>, payload: EA.Create<T>) {
    const state = ctx.getState();
    const entity = payload.entity;

    // If already exists, don't create it again.
    const found = state.entities.find(e => e.id === entity.id);
    if (found) {
      return;
    }

    // Post/create it with its service, update expiration, and make it current.
    return this.entityService.create(entity).pipe(
      tap(createdEntity => {
        // Add to current list of entities in store.
        const entities = [...state.entities, createdEntity];
        ctx.patchState({
          entities,
          expires: Date.now() + this.expiresIn,
          current: createdEntity
        });
      })
    );
  }

  @Action(EA.Update)
  update(ctx: StateContext<EntityStateModel<T>>, payload: EA.Update<T>) {
    const state = ctx.getState();
    const entity = payload.entity;

    // Put/update it with its service, update expiration, and make it current.
    return this.entityService.update(entity).pipe(
      tap(updatedEntity => {
        // Update within store and make current.
        const entities = state.entities.map(e => {
          if (e.id === updatedEntity.id) {
            return updatedEntity;
          } else {
            return e;
          }
        });

        ctx.patchState({
          entities,
          current: updatedEntity
        });
      })
    );
  }

  @Action(EA.Delete)
  delete(ctx: StateContext<EntityStateModel<T>>, payload: EA.Delete) {
    const state = ctx.getState();
    const id = payload.id;

    // Delete via service.
    return this.entityService.delete(id).pipe(
      tap(() => {
        // Remove current entities in local store.
        const entities = state.entities.filter(e => e.id !== id);
        ctx.patchState({
          entities,
        });
      })
    );
  }

}
